import { InfiniteScrollSentinel } from "@/components/shared/InfiniteScrollSentinel";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { usePreloadStashes } from "@/hooks/usePreloadStashes";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, FolderPlus, Pencil, SlidersHorizontal } from "lucide-react";
import { SearchField } from "@/features/search/SearchField";
import {
  mapPostToFeed,
  type FeedPost,
  type RecentPostsFilters,
  DEFAULT_RECENT_POSTS_PAGE_SIZE,
  useRecentPostsInfiniteQuery,
} from "@/hooks/useRecentStashes";
import { formatPlatform } from "@/lib/platforms";
import { InventorySort } from "@/components/shared/Inventory";
import { useStashesFilters } from "@/hooks/useStashesFilters";
import { ENTITY_TYPE_OPTIONS } from "@/constants/entityTypes";
import { cn } from "@/lib/utils";
import { tagsService } from "@/services/tags.service";
import { postsService } from "@/services/posts.service";
import { useShelves, buildShelfFilterState } from "@/hooks/useShelves";
import { Button, ButtonCountBadge } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PageRefreshBoundary } from "@/components/refresh/PageRefreshBoundary";
import {
  StashThumbnailGrid,
  StashThumbnailMasonry,
  ThumbnailOverlayBadge,
  type StashThumbnailData,
} from "@/components/shared/StashThumbnail";
import { EmptyState } from "@/components/shared/EmptyState";
import { StashesPageHeader } from "@/components/stashes/StashesPageHeader";
import { TagsWithShowHide } from "@/components/TagsWithShowHide";
import normalizeCategory from "@/utils/normalizeCategory";
import { useTagsWithCounts } from "@/hooks/useTagsWithCounts";
import { useToast } from "@/hooks/use-toast";
import { useTabReselect, type TabId } from "@/hooks/useTabNavigation";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useIsTabActive } from "@/contexts/TabActivityContext";
import { SCROLL_CONTAINER_PRIORITY, useScrollContainerRegistration } from "@/contexts/ScrollContainerContext";
import { MEDIA_QUERIES } from "@/constants/layout";
import { SaveToCollectionOverlay } from "@/components/collections/SaveToCollectionOverlay";
import { AddToCollectionSheet } from "@/components/AddToCollectionSheet";
import { SelectionActionBar } from "@/components/stashes/SelectionActionBar";
import { useStashSelectMode } from "@/hooks/useStashSelectMode";
import { useSearchTagIntersection } from "@/hooks/useSearchTagIntersection";
import { useSearchStashes, type SearchStashesPage } from "@/hooks/useSearchStashes";
import { buildStashesSearchParamsForInput } from "@/lib/stashesSearch";
import { TagScopeMenu } from "@/components/shared/TagScopeMenu";
import { isPostPending } from "@/utils/stashState";
import { shownTagScope, tagInScope, type TagScope } from "@/lib/tagScope";
import { TopLevelPageHeader } from "@/components/shared/TopLevelPageHeader";
import { SectionHeader } from "@/components/shared/SectionHeader";
import { ScopeTabs } from "@/components/shared/ScopeTabs";
import { SearchResults } from "@/features/search/SearchResults";
import { ExploreResults } from "@/features/search/ExploreResults";
import { ALL_PREVIEW, CORPUS_TITLES, SEARCH_DEBOUNCE_MS, type SearchCorpus, type SearchFacet } from "@/features/search/searchModel";
import { CAPTION, HEADLINE, LABEL, META, ROW_TEXT, ROW_TITLE } from "@/lib/typography";

interface PostsFeedProps {
  contentWidthClass?: string;
  pageSize?: number;
  tabId: TabId;
  /** Hide view toggle, sort, and filters. */
  showControls?: boolean;
  /** Hide tag pills row. */
  showTagPills?: boolean;
  /** Extra action element shown in the header area. */
  headerAction?: React.ReactNode;
  /** When present, render a contextual return affordance instead of the default title. */
  sourceStashId?: string;
  /** Optional return handler for contextual stash browse mode. */
  onReturnToSourceStash?: () => void;
  /** When true (default), load and display recent posts on mount. When false, start empty until a search/filter is active. */
  browseByDefault?: boolean;
  /** Override the page header title. */
  title?: string;
  /**
   * A heading over the stashes (Home's "Recent Stashes"). With one, the heading carries the
   * tag choice and the controls (native RecentHeading), and the page has a bar only for a
   * `title`.
   */
  sectionTitle?: string;
  /** Beside the stashes at desktop, a fifth of the window; above them otherwise (Home's shortcuts). */
  aside?: React.ReactNode;
  /** At desktop widths, keep the page header and aside outside the feed scroller. */
  scrollContent?: boolean;
  /** Before the section heading, in its row (Home's menu, when there are no shortcuts to lead). */
  sectionLeading?: React.ReactNode;
  /** The tag row's first choice (native: Home shows My Tags, All stashes all tags). */
  defaultTagScope?: TagScope;
  /** Replaces the empty state for an account with no stashes yet (Home's welcome). */
  emptyState?: React.ReactNode;
}

interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

interface FilterGroup {
  id: "tags" | "intents" | "types" | "categories" | "platforms" | "group";
  label: string;
  options: FilterOption[];
  selected: string[];
  onToggle: (value: string) => void;
  emptyLabel: string;
}

type FilterGroupId = FilterGroup["id"];

const formatCategoryLabel = (category: string) => {
  if (!category) return "Uncategorized";
  return normalizeCategory(category);
};

const SORT_OPTIONS: Array<{ value: "asc" | "desc"; label: string }> = [
  { value: "desc", label: "Date (Newest)" },
  { value: "asc", label: "Date (Oldest)" },
];

const formatTypeLabel = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, char => char.toUpperCase());

const parseParamValues = (searchParams: URLSearchParams, key: string) => {
  const values = searchParams
    .getAll(key)
    .flatMap(value => value.split(","))
    .map(value => value.trim())
    .filter(value => value.length > 0 && value !== "all");
  return Array.from(new Set(values));
};

const FILTER_GROUP_STORAGE_KEY = "stashes-feed:last-filter-group";

const CONTENT_WIDTH_CLASS = "w-full";

const PostsFeed = ({
  contentWidthClass = CONTENT_WIDTH_CLASS,
  pageSize = DEFAULT_RECENT_POSTS_PAGE_SIZE,
  tabId,
  showControls = true,
  showTagPills = showControls,
  headerAction,
  sourceStashId,
  onReturnToSourceStash,
  browseByDefault = true,
  title,
  sectionTitle,
  aside,
  scrollContent = false,
  sectionLeading,
  defaultTagScope = "all",
  emptyState,
}: PostsFeedProps) => {
  const isActive = useIsTabActive();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const isWideLayout = useMediaQuery(MEDIA_QUERIES.desktop);
  const scrollWithinPage = scrollContent && isWideLayout;
  const feedScrollRef = useRef<HTMLDivElement | null>(null);
  const registerFeedScroll = useScrollContainerRegistration(
    isActive && scrollWithinPage, SCROLL_CONTAINER_PRIORITY.PANEL,
  );
  const setFeedScrollRef = useCallback((element: HTMLDivElement | null) => {
    feedScrollRef.current = element;
    registerFeedScroll(element);
  }, [registerFeedScroll]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [desktopFiltersOpen, setDesktopFiltersOpen] = useState(false);
  const [expandedFilterGroups, setExpandedFilterGroups] = useState<Record<FilterGroupId, boolean>>({});
  const [lastExpandedGroupId, setLastExpandedGroupId] = useState<FilterGroupId | null>(null);
  const [isTagEditMode, setIsTagEditMode] = useState(false);
  const [tagScope, setTagScope] = useState<TagScope>(defaultTagScope);
  const [taggingTagIds, setTaggingTagIds] = useState<string[]>([]);
  const [taggingPostIds, setTaggingPostIds] = useState<Set<string>>(new Set());
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchFocusKey, setSearchFocusKey] = useState(() => Date.now().toString());

  // Multi-select for adding to collections
  const {
    isSelectMode,
    selectedIds,
    selectedCount,
    enterSelectMode,
    exitSelectMode,
    toggleSelect,
    selectAll,
    clearSelection,
  } = useStashSelectMode();
  const [collectionSheetOpen, setCollectionSheetOpen] = useState(false);

  // The field keeps raw typing; the URL owns the query that actually runs.
  const searchParam = searchParams.get("q") ?? "";
  const [searchDraft, setSearchDraft] = useState({
    input: searchParam,
    observedParam: searchParam,
    issuedParam: null as string | null,
  });
  // Our own delayed URL acknowledgement must not overwrite newer typing. A
  // genuinely new URL resets the draft before its old debounce can be committed.
  if (searchDraft.observedParam !== searchParam) {
    setSearchDraft({
      input: searchDraft.issuedParam === searchParam.trim() ? searchDraft.input : searchParam,
      observedParam: searchParam,
      issuedParam: null,
    });
  }
  const searchInput = searchDraft.input;
  const setSearchInput = useCallback((input: string) => {
    setSearchDraft(current => ({ ...current, input }));
  }, []);
  const enableTagFilters = showTagPills;
  const enableGroupFilters = browseByDefault;
  const activeSearch = searchParam.trim();
  const isSearchActive = activeSearch.length > 0;
  // The Search page's scope and kind (native SearchCorpus, SearchFacet): page state, never the URL.
  const [corpus, setCorpus] = useState<SearchCorpus>("yours");
  const [facet, setFacet] = useState<SearchFacet>("all");
  const searchesByKind = !browseByDefault && isSearchActive;
  const exploring = !browseByDefault && corpus === "public";

  const replaceParams = useCallback(
    (next: URLSearchParams) => {
      setSearchParams(next, { replace: true, state: location.state });
    },
    [setSearchParams, location.state],
  );

  const commitSearch = useCallback((value: string) => {
    const query = value.trim();
    if (query === activeSearch) return;
    setSearchDraft(current => ({ ...current, issuedParam: query }));
    replaceParams(buildStashesSearchParamsForInput(searchParams, value));
  }, [activeSearch, replaceParams, searchParams]);

  useEffect(() => {
    if (!isActive || searchInput.trim() === activeSearch) return;
    const timeout = window.setTimeout(() => commitSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [isActive, searchInput, activeSearch, commitSearch]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      if (!value.trim()) {
        setFacet("all");
        commitSearch("");
      }
    },
    [setSearchInput, commitSearch],
  );


  // Track which posts already have the selected tagging tags
  const taggingTagIdsKey = useMemo(() => [...taggingTagIds].sort().join(","), [taggingTagIds]);
  const { data: postsWithSelectedTags = new Set<string>() } = useQuery({
    queryKey: ["stashes-with-tags", taggingTagIdsKey],
    enabled: isTagEditMode && taggingTagIds.length > 0,
    queryFn: async () => {
      // For each selected tag, get ALL post IDs (exhaust pagination)
      const postIdSets = await Promise.all(taggingTagIds.map(tagId => tagsService.getAllPostIdsForTag(tagId)));
      // Find posts that have ALL selected tags (intersection)
      if (postIdSets.length === 0) return new Set<string>();
      const firstSet = new Set(postIdSets[0]);
      for (let i = 1; i < postIdSets.length; i++) {
        const currentSet = new Set(postIdSets[i]);
        for (const id of firstSet) {
          if (!currentSet.has(id)) {
            firstSet.delete(id);
          }
        }
      }
      return firstSet;
    },
    staleTime: 30_000,
  });

  const sortParam = searchParams.get("sort");
  const selectedSort: "asc" | "desc" | undefined =
    sortParam === "asc" ? "asc" : sortParam === "desc" ? "desc" : undefined;

  const selectedTypes = useMemo(() => parseParamValues(searchParams, "routing_class"), [searchParams]);
  const selectedCategories = useMemo(() => parseParamValues(searchParams, "category"), [searchParams]);
  const selectedIntents = useMemo(() => parseParamValues(searchParams, "intent"), [searchParams]);
  const tagParams = useMemo(() => parseParamValues(searchParams, "tag"), [searchParams]);
  const selectedPlatforms = useMemo(
    () => Array.from(new Set(parseParamValues(searchParams, "platform").map(value => value.toLowerCase()))),
    [searchParams],
  );

  const groupParam = useMemo(() => searchParams.get("group") ?? "", [searchParams]);
  const isGroupFilter = enableGroupFilters && groupParam.length > 0;

  const resolvedViewMode = isDesktop ? "masonry" : "grid";

  useEffect(() => {
    if (!isActive) return;
    if (!searchParams.has("tagLogic")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("tagLogic");
    replaceParams(next);
  }, [isActive, searchParams, replaceParams]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    let changed = false;

    if (!enableTagFilters && (next.has("tag") || next.has("tagLogic"))) {
      next.delete("tag");
      next.delete("tagLogic");
      changed = true;
    }

    if (!enableGroupFilters && next.has("group")) {
      next.delete("group");
      changed = true;
    }

    if (changed) {
      replaceParams(next);
    }
  }, [enableGroupFilters, enableTagFilters, replaceParams, searchParams]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem(FILTER_GROUP_STORAGE_KEY);
    if (!stored) return;
    setLastExpandedGroupId(stored as FilterGroupId);
  }, []);

  useEffect(() => {
    if (!mobileFiltersOpen) return;
    // Auto-expand any group that has active selections
    const activeGroups: Record<FilterGroupId, boolean> = {};
    if (isGroupFilter) activeGroups.group = true;
    if (tagParams.length > 0) activeGroups.tags = true;
    if (selectedIntents.length > 0) activeGroups.intents = true;
    if (selectedTypes.length > 0) activeGroups.types = true;
    if (selectedCategories.length > 0) activeGroups.categories = true;
    if (selectedPlatforms.length > 0) activeGroups.platforms = true;
    // If nothing active, fall back to last-used group
    const hasActive = Object.keys(activeGroups).length > 0;
    setExpandedFilterGroups(hasActive ? activeGroups : lastExpandedGroupId ? { [lastExpandedGroupId]: true } : {});
  }, [
    isGroupFilter,
    lastExpandedGroupId,
    mobileFiltersOpen,
    tagParams,
    selectedIntents,
    selectedTypes,
    selectedCategories,
    selectedPlatforms,
  ]);

  // Filter options and groups are slow reads only the Filters menu shows, so they
  // wait for it to open. Anything already cached still paints straight away.
  const filtersOpen = mobileFiltersOpen || desktopFiltersOpen;
  const { data: filtersData, isLoading: filtersLoading } = useStashesFilters({}, { enabled: filtersOpen });

  const filtersDisabled = filtersLoading && !filtersData;

  const { data: userShelves } = useShelves({ enabled: filtersOpen });

  const { shelfOptions, shelfTagMap } = useMemo(() => {
    return buildShelfFilterState(userShelves);
  }, [userShelves]);

  const tagStatsFilters = useMemo(() => {
    const f: { routing_class?: string; category?: string; overall_message_intent?: string; platform?: string } = {};
    if (selectedTypes.length === 1) f.routing_class = selectedTypes[0];
    if (selectedCategories.length === 1) f.category = selectedCategories[0];
    if (selectedIntents.length === 1) f.overall_message_intent = selectedIntents[0];
    if (selectedPlatforms.length === 1) f.platform = selectedPlatforms[0];
    return f;
  }, [selectedTypes, selectedCategories, selectedIntents, selectedPlatforms]);

  // Search query — runs when the user types in the search input (hoisted for tag count derivation)
  const {
    data: searchData,
    fetchNextPage: fetchNextSearchPage,
    hasNextPage: hasNextSearchPage,
    isFetchingNextPage: isFetchingNextSearchPage,
    isFetching: isSearchFetching,
    isLoading: isSearchLoading,
    isError: isSearchError,
    error: searchError,
    refetch: refetchSearch,
  } = useSearchStashes(activeSearch, pageSize, undefined, isActive && !exploring);

  const { data: tagOptions = [], isLoading: tagOptionsLoading } = useTagsWithCounts({
    filters: tagStatsFilters,
    enabled: enableTagFilters,
  });

  const filterParams = useMemo<RecentPostsFilters>(() => {
    const params: RecentPostsFilters = {};

    const routingClassFilter = selectedTypes.length === 1 ? selectedTypes[0] : undefined;
    const categoryFilter = selectedCategories.length === 1 ? selectedCategories[0] : undefined;
    const intentFilter = selectedIntents.length === 1 ? selectedIntents[0] : undefined;

    if (routingClassFilter) {
      params.routingClass = routingClassFilter;
    }

    if (categoryFilter) {
      params.category = categoryFilter;
    }

    if (intentFilter) {
      params.intent = intentFilter;
    }

    if (selectedSort) {
      params.sort = selectedSort;
    }

    return params;
  }, [selectedCategories, selectedIntents, selectedSort, selectedTypes]);

  const {
    posts: recentPosts,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isRecentLoading,
    isError: isRecentError,
    error: recentError,
    refetch: refetchRecent,
  } = useRecentPostsInfiniteQuery(pageSize, filterParams, browseByDefault);

  const handleParamChange = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(searchParams);

      if (value === null || value === "") {
        next.delete(key);
      } else {
        next.set(key, value);
      }

      next.delete("offset");

      replaceParams(next);
    },
    [searchParams, replaceParams],
  );

  const handleMultiParamChange = useCallback(
    (key: string, values: string[]) => {
      const next = new URLSearchParams(searchParams);
      const dedupedValues = Array.from(new Set(values.filter(Boolean)));

      next.delete(key);
      dedupedValues.forEach(value => {
        if (value) {
          next.append(key, value);
        }
      });

      next.delete("offset");
      replaceParams(next);
    },
    [searchParams, replaceParams],
  );

  const handleTagParamChange = useCallback(
    (tagIds: string[]) => {
      handleMultiParamChange("tag", tagIds);
    },
    [handleMultiParamChange],
  );

  const handleTypeParamChange = useCallback(
    (values: string[]) => {
      handleMultiParamChange("routing_class", values);
    },
    [handleMultiParamChange],
  );

  const handleCategoryParamChange = useCallback(
    (values: string[]) => {
      handleMultiParamChange("category", values);
    },
    [handleMultiParamChange],
  );

  const handleIntentParamChange = useCallback(
    (values: string[]) => {
      handleMultiParamChange("intent", values);
    },
    [handleMultiParamChange],
  );

  const handlePlatformParamChange = useCallback(
    (values: string[]) => {
      handleMultiParamChange("platform", values);
    },
    [handleMultiParamChange],
  );

  const toggleSelection = useCallback((value: string, selected: string[], onChange: (values: string[]) => void) => {
    if (!value) return;
    const isSelected = selected.includes(value);
    const nextValues = isSelected ? selected.filter(item => item !== value) : [...selected, value];
    onChange(nextValues);
  }, []);

  const toggleFilterGroup = useCallback((groupId: FilterGroupId) => {
    setExpandedFilterGroups(prev => ({
      ...prev,
      [groupId]: !(prev[groupId] ?? false),
    }));
    setLastExpandedGroupId(groupId);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(FILTER_GROUP_STORAGE_KEY, groupId);
    }
  }, []);

  const selectedTags = useMemo(() => (enableTagFilters ? tagParams : []), [enableTagFilters, tagParams]);

  // The tag row offers the chosen kind of tag (native StashTagRail); a selected tag
  // stays in it whatever the choice, so it can always be cleared.
  const shownScope = shownTagScope(tagScope, tagOptions);
  const scopedTagOptions = useMemo(
    () => tagOptions.filter(tag => tagInScope(tag, shownScope) || selectedTags.includes(tag.id)),
    [tagOptions, shownScope, selectedTags],
  );
  const selectedTagsKey = useMemo(() => [...selectedTags].sort().join(","), [selectedTags]);

  const isTagFilterActive = selectedTags.length > 0;

  const { filterByTags } = useSearchTagIntersection(isSearchActive, selectedTags);

  const handleSortChange = useCallback(
    (value: string) => {
      const normalized = value === "asc" ? "asc" : "desc";
      handleParamChange("sort", normalized === "desc" ? null : normalized);
    },
    [handleParamChange],
  );

  const handleTagToggle = useCallback(
    (tagId: string) => {
      toggleSelection(tagId, selectedTags, handleTagParamChange);
    },
    [handleTagParamChange, selectedTags, toggleSelection],
  );

  const handleTagRowClick = useCallback(
    (tagId: string) => {
      if (tagId === "all") {
        handleTagParamChange([]);
        return;
      }
      handleTagToggle(tagId);
    },
    [handleTagParamChange, handleTagToggle],
  );

  const handleTaggingToggle = useCallback(
    (tagId: string) => {
      toggleSelection(tagId, taggingTagIds, setTaggingTagIds);
    },
    [taggingTagIds, toggleSelection],
  );

  const handleTagEditModeToggle = useCallback(() => {
    setIsTagEditMode(prev => {
      const next = !prev;
      if (next) {
        exitSelectMode();
        handleTagParamChange([]);
        setExpandedFilterGroups(current => ({ ...current, tags: true }));
        setLastExpandedGroupId("tags");
        if (typeof window !== "undefined") {
          window.localStorage.setItem(FILTER_GROUP_STORAGE_KEY, "tags");
        }
      }
      return next;
    });
  }, [handleTagParamChange, exitSelectMode]);

  const handleEnterSelectMode = useCallback(() => {
    setIsTagEditMode(false);
    setMobileFiltersOpen(false);
    enterSelectMode();
  }, [enterSelectMode]);

  const handleTypeToggle = useCallback(
    (value: string) => {
      toggleSelection(value, selectedTypes, handleTypeParamChange);
    },
    [handleTypeParamChange, selectedTypes, toggleSelection],
  );

  const handleCategoryToggle = useCallback(
    (value: string) => {
      toggleSelection(value, selectedCategories, handleCategoryParamChange);
    },
    [handleCategoryParamChange, selectedCategories, toggleSelection],
  );

  const handleIntentToggle = useCallback(
    (value: string) => {
      toggleSelection(value, selectedIntents, handleIntentParamChange);
    },
    [handleIntentParamChange, selectedIntents, toggleSelection],
  );

  const handlePlatformToggle = useCallback(
    (value: string) => {
      toggleSelection(value, selectedPlatforms, handlePlatformParamChange);
    },
    [handlePlatformParamChange, selectedPlatforms, toggleSelection],
  );

  const handleGroupToggle = useCallback(
    (value: string) => {
      const next = new URLSearchParams(searchParams);
      if (groupParam === value) {
        next.delete("group");
        next.delete("tag");
      } else {
        next.set("group", value);
        next.delete("tag");
        const tagIds = shelfTagMap.get(value) ?? [];
        tagIds.forEach(tagId => next.append("tag", tagId));
      }
      next.delete("offset");
      replaceParams(next);
    },
    [groupParam, searchParams, replaceParams, shelfTagMap],
  );

  const handleFiltersReset = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("sort");
    next.delete("intent");
    next.delete("routing_class");
    next.delete("category");
    next.delete("platform");
    next.delete("tag");
    next.delete("group");
    next.delete("tagLogic");
    next.delete("offset");
    next.delete("q");
    setSearchInput("");
    replaceParams(next);
  }, [searchParams, replaceParams, setSearchInput]);

  const intentOptions = useMemo(() => {
    const entries = filtersData?.intent
      ? Object.entries(filtersData.intent)
          .filter(([value]) => Boolean(value))
          .map(([value, count]) => ({
            value,
            count,
            label: formatPlatform(value),
          }))
          .sort((a, b) => {
            if (b.count !== a.count) return b.count - a.count;
            return a.value.localeCompare(b.value);
          })
      : [];

    const existing = new Set(entries.map(option => option.value));
    selectedIntents.forEach(value => {
      if (!existing.has(value)) {
        entries.unshift({ value, count: 0, label: formatPlatform(value) });
      }
    });

    return entries;
  }, [filtersData, selectedIntents]);

  const typeOptions = useMemo(() => {
    let entries = filtersData?.routing
      ? Object.entries(filtersData.routing)
          .filter(([value]) => Boolean(value))
          .map(([value, count]) => ({ value, count, label: formatTypeLabel(value) }))
          .sort((a, b) => {
            if (b.count !== a.count) return b.count - a.count;
            return a.value.localeCompare(b.value);
          })
      : [];

    if (entries.length === 0) {
      entries = ENTITY_TYPE_OPTIONS.filter(option => option.value !== "all").map(option => ({
        value: option.value,
        count: undefined,
        label: option.label,
      }));
    }

    const existing = new Set(entries.map(option => option.value));
    selectedTypes.forEach(value => {
      if (!existing.has(value)) {
        entries.unshift({ value, count: 0, label: formatTypeLabel(value) });
      }
    });

    return entries;
  }, [filtersData, selectedTypes]);

  const categoryEntries = useMemo(() => {
    const entries = filtersData?.category
      ? Object.entries(filtersData.category)
          .map(([value, count]) => ({ value, count, label: formatCategoryLabel(value) }))
          .sort((a, b) => {
            if (b.count !== a.count) return b.count - a.count;
            return a.label.localeCompare(b.label);
          })
      : [];

    const existing = new Set(entries.map(entry => entry.value));
    selectedCategories.forEach(value => {
      if (value && !existing.has(value)) {
        entries.unshift({ value, count: 0, label: formatCategoryLabel(value) });
      }
    });

    return entries;
  }, [filtersData, selectedCategories]);

  const tagFilterOptions = useMemo(() => {
    return tagOptions.map(tag => ({
      value: tag.id,
      label: tag.name,
      count: tag.count,
    }));
  }, [tagOptions]);

  const tagGroupSelected = isTagEditMode ? taggingTagIds : selectedTags;
  const tagGroupToggle = isTagEditMode ? handleTaggingToggle : handleTagToggle;
  const tagGroupEmptyLabel = tagOptionsLoading
    ? "Loading tags..."
    : isTagEditMode
      ? "Select tags to apply."
      : "No tags yet";

  const activePlatformCount = selectedPlatforms.length;
  const activeFiltersTotal =
    (isGroupFilter ? 1 : selectedTags.length) +
    selectedIntents.length +
    selectedTypes.length +
    selectedCategories.length +
    activePlatformCount;
  const hasAnyFilters = activeFiltersTotal > 0 || isSearchActive;
  const hasActiveSort = selectedSort !== undefined;

  const baseFilterGroups = useMemo<FilterGroup[]>(() => {
    const groups: FilterGroup[] = [];

    if (enableTagFilters) {
      groups.push({
        id: "tags",
        label: "Tags",
        options: tagFilterOptions,
        selected: tagGroupSelected,
        onToggle: tagGroupToggle,
        emptyLabel: tagGroupEmptyLabel,
      });
    }

    groups.push(
      {
        id: "intents",
        label: "Intents",
        options: intentOptions,
        selected: selectedIntents,
        onToggle: handleIntentToggle,
        emptyLabel: filtersDisabled ? "Loading intents..." : "No intents yet",
      },
      {
        id: "types",
        label: "Types",
        options: typeOptions,
        selected: selectedTypes,
        onToggle: handleTypeToggle,
        emptyLabel: filtersDisabled ? "Loading types..." : "No types yet",
      },
      {
        id: "categories",
        label: "Categories",
        options: categoryEntries,
        selected: selectedCategories,
        onToggle: handleCategoryToggle,
        emptyLabel: filtersDisabled ? "Loading categories..." : "No categories yet",
      },
    );

    return groups;
  }, [
    categoryEntries,
    enableTagFilters,
    filtersDisabled,
    handleCategoryToggle,
    handleIntentToggle,
    handleTypeToggle,
    intentOptions,
    isTagEditMode,
    tagGroupEmptyLabel,
    tagGroupSelected,
    tagGroupToggle,
    selectedCategories,
    selectedIntents,
    selectedTypes,
    tagFilterOptions,
    tagOptionsLoading,
    typeOptions,
  ]);

  const normalizedTypeSelections = useMemo(() => selectedTypes.map(value => value.toLowerCase()), [selectedTypes]);
  const normalizedCategorySelections = useMemo(
    () => selectedCategories.map(value => value.toLowerCase()),
    [selectedCategories],
  );
  const normalizedIntentSelections = useMemo(
    () => selectedIntents.map(value => value.toLowerCase()),
    [selectedIntents],
  );
  const normalizedPlatformSelections = useMemo(
    () => selectedPlatforms.map(value => value.toLowerCase()),
    [selectedPlatforms],
  );

  const getPostPlatform = useCallback((post: FeedPost) => {
    const candidates = [
      typeof post.platform === "string" ? post.platform : "",
      typeof post.source === "string" ? post.source : (post.source?.platform ?? ""),
    ];

    const match = candidates.find(value => value && value.toLowerCase() !== "unknown");
    return match ? match : undefined;
  }, []);

  const matchesBaseFilters = useCallback(
    (post: FeedPost) => {
      if (
        normalizedTypeSelections.length === 0 &&
        normalizedCategorySelections.length === 0 &&
        normalizedIntentSelections.length === 0
      ) {
        return true;
      }

      // Use type assertion for accessing dynamic properties that may exist on the post
      const autoCategories: Array<Record<string, unknown>> = Array.isArray(post.auto_categories)
        ? post.auto_categories
        : [];

      if (normalizedTypeSelections.length > 0) {
        const routingValues = autoCategories
          .map(category => category?.routing_class)
          .filter((value): value is string => typeof value === "string");
        if (typeof post.routing_class === "string") {
          routingValues.push(post.routing_class);
        }

        const hasMatch = routingValues.some(value => normalizedTypeSelections.includes(value.toLowerCase()));
        if (!hasMatch) return false;
      }

      if (normalizedCategorySelections.length > 0) {
        const categoryValues = autoCategories
          .map(category => category?.category)
          .filter((value): value is string => typeof value === "string");
        if (typeof post.activity_type === "string") {
          categoryValues.push(post.activity_type);
        }
        if (typeof post.category === "string") {
          categoryValues.push(post.category);
        }

        const hasMatch = categoryValues.some(value => normalizedCategorySelections.includes(value.toLowerCase()));
        if (!hasMatch) return false;
      }

      if (normalizedIntentSelections.length > 0) {
        const intentValue =
          typeof post.overall_message_intent === "string"
            ? post.overall_message_intent
            : typeof post.intent === "string"
              ? post.intent
              : undefined;

        if (!intentValue || !normalizedIntentSelections.includes(intentValue.toLowerCase())) {
          return false;
        }
      }

      return true;
    },
    [normalizedCategorySelections, normalizedIntentSelections, normalizedTypeSelections],
  );

  const matchesPlatformFilter = useCallback(
    (post: FeedPost) => {
      if (normalizedPlatformSelections.length === 0) {
        return true;
      }
      const platformValue = getPostPlatform(post);
      if (!platformValue) return false;
      return normalizedPlatformSelections.includes(platformValue.toLowerCase());
    },
    [getPostPlatform, normalizedPlatformSelections],
  );

  const baseRecentPosts = useMemo(() => recentPosts.filter(matchesBaseFilters), [recentPosts, matchesBaseFilters]);

  const filteredRecentPosts = useMemo(
    () => baseRecentPosts.filter(matchesPlatformFilter),
    [baseRecentPosts, matchesPlatformFilter],
  );

  const selectedTypesKey = useMemo(() => [...selectedTypes].sort().join(","), [selectedTypes]);
  const selectedCategoriesKey = useMemo(() => [...selectedCategories].sort().join(","), [selectedCategories]);
  const selectedIntentsKey = useMemo(() => [...selectedIntents].sort().join(","), [selectedIntents]);

  // Tag filtering with cursor-based pagination (matches backend pagination)
  const tagStashesQuery = useInfiniteQuery({
    queryKey: [
      "stashes",
      "tags",
      selectedTagsKey,
      selectedSort ?? "desc",
      selectedTypesKey,
      selectedCategoriesKey,
      selectedIntentsKey,
    ],
    enabled: isTagFilterActive,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam: cursor }) => {
      // For single tag, use cursor pagination directly
      // For multi-tag, fetch all IDs from each tag then paginate client-side
      if (selectedTags.length === 1) {
        const tagId = selectedTags[0];
        const response = await tagsService.getPostsForTag(tagId, { cursor, limit: 50 });
        const postIds = response.post_ids;

        if (postIds.length === 0) {
          return { posts: [] as FeedPost[], cursor: undefined, hasMore: false };
        }

        const { posts: postsMap } = await postsService.batchGetPosts(postIds);

        const mapped = Object.values(postsMap ?? {})
          .map(entry =>
            entry && typeof entry === "object" && "post" in entry ? (entry as Record<string, unknown>).post : entry,
          )
          .filter((post): post is NonNullable<typeof post> => Boolean(post))
          .map(mapPostToFeed)
          .filter(matchesBaseFilters);

        const sortOrder = selectedSort ?? "desc";
        mapped.sort((a, b) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
        });

        return { posts: mapped, cursor: response.cursor, hasMore: response.hasMore };
      }

      // Multi-tag: fetch all IDs from each tag (no cursor — union requires full scan)
      const postIdSets = await Promise.all(selectedTags.map(tagId => tagsService.getPostsForTag(tagId)));
      const allPostIds = new Set<string>();
      postIdSets.forEach(({ post_ids }) => {
        post_ids.forEach(id => allPostIds.add(id));
      });

      const postIds = Array.from(allPostIds);
      if (postIds.length === 0) {
        return { posts: [] as FeedPost[], cursor: undefined, hasMore: false };
      }

      const { posts: postsMap } = await postsService.batchGetPosts(postIds);

      const mapped = Object.values(postsMap ?? {})
        .map(entry =>
          entry && typeof entry === "object" && "post" in entry ? (entry as Record<string, unknown>).post : entry,
        )
        .filter((post): post is NonNullable<typeof post> => Boolean(post))
        .map(mapPostToFeed)
        .filter(matchesBaseFilters);

      const sortOrder = selectedSort ?? "desc";
      mapped.sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
      });

      return { posts: mapped, cursor: undefined, hasMore: false };
    },
    getNextPageParam: lastPage => (lastPage.hasMore ? lastPage.cursor : undefined),
    staleTime: 60_000,
  });

  const {
    isLoading: isTagLoading,
    isError: isTagError,
    error: tagError,
    refetch: refetchTagPosts,
    isFetching: isTagFetching,
    fetchNextPage: fetchNextTagPage,
    hasNextPage: hasNextTagPage,
    isFetchingNextPage: isFetchingNextTagPage,
  } = tagStashesQuery;

  const baseTagPosts = useMemo(() => {
    if (!tagStashesQuery.data?.pages) return [] as FeedPost[];
    const flattened = tagStashesQuery.data.pages.flatMap(page => page.posts);
    const seen = new Set<string>();
    return flattened.filter(post => {
      if (seen.has(post.id)) return false;
      seen.add(post.id);
      return true;
    });
  }, [tagStashesQuery.data]);
  const filteredTagPosts = useMemo(
    () => baseTagPosts.filter(matchesPlatformFilter),
    [baseTagPosts, matchesPlatformFilter],
  );

  // Search results — dedup, apply client-side filters, intersect with tags if needed
  const searchPosts = useMemo(() => {
    if (!searchData?.pages) return [] as FeedPost[];
    const pages = searchData.pages as SearchStashesPage[];
    const flattened = pages.flatMap(page => page.posts);
    const seen = new Set<string>();
    return flattened.filter(post => {
      if (seen.has(post.id)) return false;
      seen.add(post.id);
      return true;
    });
  }, [searchData]);

  const filteredSearchPosts = useMemo(() => {
    const result = searchPosts.filter(matchesBaseFilters).filter(matchesPlatformFilter);
    return filterByTags ? filterByTags(result) : result;
  }, [searchPosts, matchesBaseFilters, matchesPlatformFilter, filterByTags]);

  const posts = isSearchActive
    ? filteredSearchPosts
    : isTagFilterActive
      ? filteredTagPosts
      : browseByDefault
        ? filteredRecentPosts
        : [];
  // The heading's ellipsis means the server says a save is still being processed: on
  // while its pipeline works, off when it finishes, never lit by list refetching.
  const savesInFlight = posts.some(isPostPending);

  const handleSelectAll = useCallback(() => {
    selectAll(posts.map(p => p.id));
  }, [selectAll, posts]);

  // Check if any selected post is private (for collection privacy warning)
  const batchItemVisibility = useMemo<"private" | "public" | undefined>(() => {
    if (selectedIds.size === 0) return undefined;
    const hasPrivate = posts.some(
      p => selectedIds.has(p.id) && (p as Record<string, unknown>).visibility === "private",
    );
    return hasPrivate ? "private" : "public";
  }, [posts, selectedIds]);

  const basePostsForPlatform = useMemo(
    () => (isTagFilterActive ? baseTagPosts : baseRecentPosts),
    [baseRecentPosts, baseTagPosts, isTagFilterActive],
  );

  const platformOptions = useMemo(() => {
    const counts = new Map<string, number>();

    basePostsForPlatform.forEach(post => {
      const platformValue = getPostPlatform(post);
      if (!platformValue) return;
      const normalized = platformValue.toLowerCase();
      if (!normalized || normalized === "unknown") return;
      counts.set(normalized, (counts.get(normalized) ?? 0) + 1);
    });

    const entries = Array.from(counts.entries())
      .map(([value, count]) => ({
        value,
        count,
        label: formatPlatform(value),
      }))
      .sort((a, b) => {
        if ((b.count ?? 0) !== (a.count ?? 0)) {
          return (b.count ?? 0) - (a.count ?? 0);
        }
        return a.label.localeCompare(b.label);
      });

    const existing = new Set(entries.map(option => option.value));
    selectedPlatforms.forEach(value => {
      const normalized = value.toLowerCase();
      if (!existing.has(normalized)) {
        entries.unshift({ value: normalized, count: 0, label: formatPlatform(value) });
      }
    });

    return entries;
  }, [basePostsForPlatform, getPostPlatform, selectedPlatforms]);

  const platformFilterGroup = useMemo<FilterGroup>(
    () => ({
      id: "platforms",
      label: "Platform",
      options: platformOptions,
      selected: selectedPlatforms,
      onToggle: handlePlatformToggle,
      emptyLabel: "No platforms yet",
    }),
    [handlePlatformToggle, platformOptions, selectedPlatforms],
  );

  const groupFilterGroup = useMemo<FilterGroup>(
    () => ({
      id: "group",
      label: "Groups",
      options: shelfOptions,
      selected: isGroupFilter ? [groupParam] : [],
      onToggle: handleGroupToggle,
      emptyLabel: "No groups yet",
    }),
    [groupParam, isGroupFilter, handleGroupToggle, shelfOptions],
  );

  const filterGroups = useMemo(
    () =>
      enableGroupFilters && shelfOptions.length > 0
        ? [groupFilterGroup, platformFilterGroup, ...baseFilterGroups]
        : [platformFilterGroup, ...baseFilterGroups],
    [baseFilterGroups, enableGroupFilters, groupFilterGroup, platformFilterGroup, shelfOptions.length],
  );

  const feedIsLoading = isSearchActive
    ? isSearchLoading
    : isTagFilterActive
      ? isTagLoading || (isTagFetching && baseTagPosts.length === 0)
      : isRecentLoading;
  const feedIsError = isSearchActive ? isSearchError : isTagFilterActive ? isTagError : isRecentError;
  const feedError = isSearchActive ? searchError : isTagFilterActive ? tagError : recentError;
  const feedIsFetchingNext = isSearchActive
    ? isFetchingNextSearchPage
    : isTagFilterActive
      ? isFetchingNextTagPage
      : isFetchingNextPage;
  const feedHasNextPage = isSearchActive
    ? (hasNextSearchPage ?? false)
    : isTagFilterActive
      ? (hasNextTagPage ?? false)
      : hasNextPage;
  const showEmptyState =
    browseByDefault &&
    !feedIsLoading &&
    !feedIsError &&
    posts.length === 0 &&
    !hasAnyFilters &&
    !isSearchActive;
  const showSearchIdleState = !browseByDefault && !isSearchActive && !hasAnyFilters && !isTagFilterActive;

  const renderSortControl = () => (
    <InventorySort
      label="Sort stashes"
      value={selectedSort ?? "desc"}
      onChange={handleSortChange}
      options={Object.fromEntries(SORT_OPTIONS.map(option => [option.value, { label: option.label }]))}
    />
  );

  const renderDesktopFilterMenu = () => (
    <DropdownMenu
      onOpenChange={open => {
        setDesktopFiltersOpen(open);
        if (open) {
          setExpandedFilterGroups(
            isGroupFilter ? { group: true } : lastExpandedGroupId ? { [lastExpandedGroupId]: true } : {},
          );
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-control"
          aria-label={activeFiltersTotal > 0 ? `Filters: ${activeFiltersTotal} active` : "Filters"}
          title={activeFiltersTotal > 0 ? `Filters: ${activeFiltersTotal} active` : "Filters"}
        >
          <SlidersHorizontal aria-hidden="true" />
          {activeFiltersTotal > 0 && (
            <ButtonCountBadge>{activeFiltersTotal}</ButtonCountBadge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[320px] max-h-[70vh] overflow-y-auto z-[60]">
        <div className="flex items-center justify-between px-2 py-2 rounded-md bg-muted/40">
          <span className="text-ui-micro font-semibold uppercase tracking-wide text-muted-foreground">Filters</span>
          {(hasAnyFilters || hasActiveSort) && (
            <button type="button" onClick={handleFiltersReset} className={CAPTION}>
              Reset
            </button>
          )}
        </div>
        <DropdownMenuSeparator />
        {filterGroups.map((group, index) => {
          const isOpen = expandedFilterGroups[group.id] ?? false;
          return (
            <div key={group.id} className="py-1">
              <div className="flex items-center gap-2 px-2">
                <button
                  type="button"
                  onClick={() => toggleFilterGroup(group.id)}
                  className={cn(LABEL, "flex flex-1 items-center justify-between py-1 uppercase text-muted-foreground")}
                >
                  <span>{group.label}</span>
                  <ChevronDown className={cn("h-3 w-3 transition-transform", isOpen && "rotate-180")} />
                </button>
                {group.id === "tags" && (
                  <button
                    type="button"
                    onClick={handleTagEditModeToggle}
                    className={cn(
                      "flex h-6 w-6 items-center justify-center rounded-md border border-transparent text-muted-foreground",
                      "",
                      isTagEditMode && "bg-muted text-foreground",
                    )}
                    aria-pressed={isTagEditMode}
                    aria-label="Toggle tag edit mode"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                )}
              </div>
              {isOpen && (
                <div className="mt-1 space-y-0.5">
                  {isTagEditMode && group.id === "tags" && (
                    <div className="px-2 pb-1 text-ui-micro text-muted-foreground">
                      Select tags, then click a post to apply.
                    </div>
                  )}
                  {group.options.length === 0 ? (
                    <div className={cn(CAPTION, "px-2 py-2")}>{group.emptyLabel}</div>
                  ) : (
                    group.options.map(option => (
                      <DropdownMenuCheckboxItem
                        key={`${group.id}-${option.value}`}
                        checked={group.selected.includes(option.value)}
                        onCheckedChange={() => group.onToggle(option.value)}
                      >
                        <span className="flex-1 truncate">{option.label}</span>
                        {typeof option.count === "number" && (
                          <span className={cn(CAPTION, "ml-auto")}>{option.count}</span>
                        )}
                      </DropdownMenuCheckboxItem>
                    ))
                  )}
                </div>
              )}
              {index < filterGroups.length - 1 && <DropdownMenuSeparator />}
            </div>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const addToCollectionDisabled = !browseByDefault && posts.length === 0;

  // View tools share the same icon treatment across desktop and phone layouts.
  const renderAddToCollectionTrigger = () => (
    <Button
      type="button"
      variant="ghost"
      size="icon-control"
      onClick={handleEnterSelectMode}
      disabled={addToCollectionDisabled}
      aria-label="Save to Collection"
      title="Save to Collection"
    >
      <FolderPlus aria-hidden="true" />
    </Button>
  );

  const mobileFilterCount = activeFiltersTotal + (hasActiveSort ? 1 : 0);
  const renderMobileFilterTrigger = () => (
    <Button
      type="button"
      variant="ghost"
      size="icon-control"
      onClick={() => setMobileFiltersOpen(true)}
      aria-label={mobileFilterCount > 0 ? `Filters and sort: ${mobileFilterCount} active` : "Filters and sort"}
      title={mobileFilterCount > 0 ? `Filters and sort: ${mobileFilterCount} active` : "Filters and sort"}
    >
      <SlidersHorizontal aria-hidden="true" />
      {mobileFilterCount > 0 && (
        <ButtonCountBadge>{mobileFilterCount}</ButtonCountBadge>
      )}
    </Button>
  );

  const renderSelectCancelControl = () => (
    <Button
      type="button"
      onClick={exitSelectMode}
      variant="ghost"
      size="control"
    >
      Cancel
    </Button>
  );

  const tagScopeMenu =
    browseByDefault && showTagPills && tagOptions.length > 0 ? (
      <TagScopeMenu value={tagScope} onChange={setTagScope} tags={tagOptions} />
    ) : null;

  const headerControls = showControls ? (
    <div className="action-row">
      {!sectionTitle && tagScopeMenu}
      {headerAction}
      {browseByDefault && (
        <div className="flex items-center lg:hidden">
          {isSelectMode ? (
            renderSelectCancelControl()
          ) : (
            <>
              {renderAddToCollectionTrigger()}
              {renderMobileFilterTrigger()}
            </>
          )}
        </div>
      )}
      <div className="hidden items-center lg:flex">
        {isSelectMode ? renderSelectCancelControl() : renderAddToCollectionTrigger()}
        {renderSortControl()}
        {renderDesktopFilterMenu()}
      </div>
    </div>
  ) : undefined;

  usePreloadStashes(posts);

  const applyTagsToPost = useCallback(
    async (postId: string) => {
      if (taggingTagIds.length === 0) {
        toast({ description: "Select tags to apply first." });
        return;
      }
      if (taggingPostIds.has(postId)) return;

      // Check if post already has all selected tags (for toggle logic)
      const alreadyHasAllTags = postsWithSelectedTags.has(postId);

      setTaggingPostIds(prev => {
        const next = new Set(prev);
        next.add(postId);
        return next;
      });

      // Optimistically update the visual state
      queryClient.setQueryData<Set<string>>(["stashes-with-tags", taggingTagIdsKey], prev => {
        const next = new Set(prev);
        if (alreadyHasAllTags) {
          next.delete(postId);
        } else {
          next.add(postId);
        }
        return next;
      });

      try {
        const existingTags = await tagsService.getPostTags(postId);
        const existingIds = existingTags.map(tag => tag.id);

        let newTagIds: string[];
        if (alreadyHasAllTags) {
          // Remove the selected tags
          const taggingSet = new Set(taggingTagIds);
          newTagIds = existingIds.filter(id => !taggingSet.has(id));
        } else {
          // Add the selected tags
          newTagIds = Array.from(new Set([...existingIds, ...taggingTagIds]));
        }

        await tagsService.setPostTags(postId, newTagIds);
        // No toast needed - visual feedback is sufficient
      } catch (error) {
        console.error("Failed to update tags:", error);
        toast({ description: "Failed to update tags." });
        // Revert optimistic update on error
        queryClient.setQueryData<Set<string>>(["stashes-with-tags", taggingTagIdsKey], prev => {
          const next = new Set(prev);
          if (alreadyHasAllTags) {
            next.add(postId); // Revert removal
          } else {
            next.delete(postId); // Revert addition
          }
          return next;
        });
      } finally {
        setTaggingPostIds(prev => {
          const next = new Set(prev);
          next.delete(postId);
          return next;
        });
      }
    },
    [taggingPostIds, taggingTagIds, postsWithSelectedTags, taggingTagIdsKey, queryClient, toast],
  );

  const handleTagApply = useCallback(
    (postId: string, e?: React.MouseEvent) => {
      const activeElement = document.activeElement as HTMLElement | null;
      if (activeElement && typeof activeElement.blur === "function") {
        activeElement.blur();
      }
      e?.preventDefault();
      e?.stopPropagation();
      void applyTagsToPost(postId);
    },
    [applyTagsToPost],
  );

  const handleLoadMore = useCallback(() => {
    if (!feedHasNextPage || feedIsFetchingNext || feedIsLoading) {
      return;
    }
    if (isSearchActive) {
      void fetchNextSearchPage();
    } else if (isTagFilterActive) {
      void fetchNextTagPage();
    } else {
      void fetchNextPage();
    }
  }, [
    feedHasNextPage,
    feedIsFetchingNext,
    feedIsLoading,
    isSearchActive,
    fetchNextSearchPage,
    isTagFilterActive,
    fetchNextTagPage,
    fetchNextPage,
  ]);

  const handleRefresh = useCallback(async () => {
    if (exploring && isSearchActive) {
      await queryClient.refetchQueries({ queryKey: ["search", "public", activeSearch], exact: true, type: "active" });
    } else if (isSearchActive) {
      await refetchSearch();
    } else if (isTagFilterActive) {
      await refetchTagPosts();
    } else {
      await refetchRecent();
    }
  }, [
    exploring,
    queryClient,
    activeSearch,
    isSearchActive,
    refetchSearch,
    isTagFilterActive,
    refetchTagPosts,
    refetchRecent,
  ]);

  const resetFeedToDefault = useCallback(() => {
    setMobileFiltersOpen(false);
    setIsTagEditMode(false);
    exitSelectMode();
    setSearchInput("");
    setSearchParams(new URLSearchParams(), { replace: true });
    void refetchRecent();
  }, [refetchRecent, setSearchParams, exitSelectMode, setSearchInput]);

  const focusSearchInput = useCallback(() => {
    setSearchFocusKey(Date.now().toString());
  }, []);

  const handleTabReselect = useCallback(() => {
    if (!browseByDefault) {
      focusSearchInput();
      return;
    }
    const resetActions: Partial<Record<TabId, () => void>> = {
      home: resetFeedToDefault,
      stashes: resetFeedToDefault,
    };

    resetActions[tabId]?.();
  }, [browseByDefault, focusSearchInput, resetFeedToDefault, tabId]);

  useTabReselect(tabId, handleTabReselect);

  // Focus search input when tab becomes active
  useEffect(() => {
    if (!browseByDefault && isActive) {
      focusSearchInput();
    }
  }, [browseByDefault, isActive, focusSearchInput]);

  // FeedPost is structurally compatible with StashThumbnailData
  const thumbnailPosts = posts as unknown as StashThumbnailData[];

  const FeedFooter = useCallback(() => {
    if (isSearchActive && isSearchFetching) {
      return <p className={cn(META, "loading-dots text-center py-4 w-full")}>Searching</p>;
    }
    if (!feedHasNextPage && posts.length > 0 && !feedIsFetchingNext) {
      return <p className={cn(META, "text-center py-4 w-full")}>You're all caught up.</p>;
    }
    return null;
  }, [feedHasNextPage, feedIsFetchingNext, posts.length, isSearchActive, isSearchFetching]);

  // Every stash list this feed draws: the window's masonry, or the grid on a phone, with
  // the overlay its mode calls for. `continues` reads the next page at the end.
  const PostsView = resolvedViewMode === "masonry" ? StashThumbnailMasonry : StashThumbnailGrid;
  const renderPosts = (list: StashThumbnailData[], continues: boolean) => (
    <>
      <PostsView
        posts={list}
        isEditMode={isTagEditMode || isSelectMode}
        selectedIds={isSelectMode ? selectedIds : undefined}
        onToggleSelect={isTagEditMode ? handleTagApply : isSelectMode ? toggleSelect : undefined}
        getItemOverlay={
          isTagEditMode
            ? post =>
                taggingTagIds.length > 0 && postsWithSelectedTags.has(post.id) ? (
                  <ThumbnailOverlayBadge variant="tagged" position="bottom-left" />
                ) : null
            : isSelectMode
              ? undefined
              : post => (
                  <SaveToCollectionOverlay
                    itemId={post.id}
                    itemType="stash"
                    itemVisibility={(post as Record<string, unknown>).visibility as "private" | "public" | undefined}
                  />
                )
        }
        getItemClassName={
          isTagEditMode
            ? post => (taggingTagIds.length > 0 && postsWithSelectedTags.has(post.id) ? "opacity-60" : undefined)
            : undefined
        }
      />
      {continues && (
        <>
          <InfiniteScrollSentinel enabled={isActive} hasMore={!!feedHasNextPage}
            isLoading={feedIsFetchingNext} onLoadMore={handleLoadMore}
            root={scrollWithinPage ? feedScrollRef.current : undefined}
            cursor={`${list[0]?.id ?? ""}:${list.length}`} />
          <FeedFooter />
        </>
      )}
    </>
  );

  return (
    <>
      <PageRefreshBoundary onRefresh={handleRefresh} className={scrollContent ? "page-workspace-boundary" : undefined}>
        <div className={cn("page-frame page-bottom flex flex-col overflow-x-clip", scrollContent && "page-workspace")} data-page={tabId}>
          <div className="page-header-surface">
            <div className="page-safe-spacer" />
            {(browseByDefault && sectionTitle) || !browseByDefault ? (
              <TopLevelPageHeader
                title={title}
                showSafeAreaSpacer={false}
                wrapperClassName={contentWidthClass}
                search={<SearchField value={searchInput} onChange={handleSearchChange}
                  onSubmit={browseByDefault ? undefined : commitSearch}
                  focusKey={browseByDefault || searchParam ? undefined : searchFocusKey}
                  disabledSuggestions={exploring} pageSize={pageSize}
                  placeholder={exploring ? "Explore stashes" : "Search Stasht"} />}
                action={!browseByDefault && !exploring ? (isSelectMode ? renderSelectCancelControl() : renderAddToCollectionTrigger()) : undefined}
              />
            ) : (
              <StashesPageHeader
                loading={savesInFlight}
                contentWidthClass={contentWidthClass}
                controls={headerControls}
                showControls={showControls}
                sourceStashId={sourceStashId}
                onReturnToSourceStash={onReturnToSourceStash}
                title={title}
                search={<SearchField value={searchInput} onChange={handleSearchChange} pageSize={pageSize} />}
              />
            )}
          </div>

          <div className="page-columns">
          {aside && <div className="page-columns-aside scrollbar-hide">{aside}</div>}
          <div ref={setFeedScrollRef} className="page-columns-content scrollbar-hide"
            tabIndex={scrollWithinPage ? 0 : undefined}
            role={scrollWithinPage ? "region" : undefined}
            aria-label={scrollWithinPage ? sectionTitle ?? title ?? "Stashes" : undefined}>

          {sectionTitle && (
            // A bar's height, level with the aside's first heading.
            <div className="page-section-heading">
              {sectionLeading}
              <SectionHeader
                title={sectionTitle}
                loading={savesInFlight}
                action={
                  <>
                    {tagScopeMenu}
                    {headerControls}
                  </>
                }
                className={cn("min-w-0 flex-1", contentWidthClass)}
              />
            </div>
          )}

          {/* Inline tag pills (stashes browse — above content) */}
          {browseByDefault && showTagPills && (
            <div className={cn("w-full", contentWidthClass)}>
              {tagOptionsLoading ? null : tagOptions.length > 0 ? (
                <TagsWithShowHide
                  tags={scopedTagOptions}
                  selectedTag={selectedTags}
                  onTagClick={handleTagRowClick}
                  tagDisplayStyle="colored"
                />
              ) : null}
            </div>
          )}

          {!browseByDefault && (
            <ScopeTabs
              label="Search scope"
              value={corpus}
              onChange={next => {
                exitSelectMode();
                setCorpus(next);
              }}
              tabs={[
                { value: "yours", label: CORPUS_TITLES.yours },
                { value: "public", label: CORPUS_TITLES.public, explore: true },
              ]}
              className={cn("search-corpus-tabs", contentWidthClass)}
            />
          )}

          {/* Tag pills below search bar (search page) */}
          {!browseByDefault && showTagPills && (
            <div className={cn("w-full", contentWidthClass)}>
              {tagOptionsLoading ? null : tagOptions.length > 0 ? (
                <TagsWithShowHide
                  tags={tagOptions}
                  selectedTag={selectedTags}
                  onTagClick={handleTagRowClick}
                  tagDisplayStyle="colored"
                />
              ) : null}
            </div>
          )}

          {showControls && (
            <Drawer open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
              <DrawerContent className="max-h-[85vh] max-w-2xl mx-auto">
                <DrawerHeader className="px-4 pt-2 pb-3 border-b bg-muted/40">
                  <div className="flex items-center justify-between">
                    <DrawerClose asChild>
                      <Button variant="ghost" size="sm" className="h-8 px-2">
                        Close
                      </Button>
                    </DrawerClose>
                    <DrawerTitle className={HEADLINE}>Filters</DrawerTitle>
                    {hasAnyFilters || hasActiveSort ? (
                      <Button variant="ghost" size="sm" className="h-8 px-2" onClick={handleFiltersReset}>
                        Reset
                      </Button>
                    ) : (
                      <span className="w-12" aria-hidden="true" />
                    )}
                  </div>
                </DrawerHeader>
                <ScrollArea className="flex-1 px-4">
                  <div className="space-y-6 pb-6 pt-4">
                    {/* Sort */}
                    <div className="space-y-3">
                      <span className={cn(ROW_TITLE, "text-foreground")}>Sort</span>
                      {SORT_OPTIONS.map(option => {
                        const isActive = (selectedSort ?? "desc") === option.value;
                        return (
                          <div key={option.value} className="flex items-center justify-between gap-3">
                            <Label className={cn(ROW_TEXT, "flex-1 font-normal text-foreground")}>{option.label}</Label>
                            <Checkbox checked={isActive} onCheckedChange={() => handleSortChange(option.value)} />
                          </div>
                        );
                      })}
                    </div>
                    {filterGroups.map(group => {
                      const isOpen = expandedFilterGroups[group.id] ?? false;
                      const selectedCount = group.selected.length;
                      return (
                        <div key={group.id} className="space-y-3">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => toggleFilterGroup(group.id)}
                              className={cn(ROW_TITLE, "flex flex-1 items-center justify-between text-foreground")}
                            >
                              <span className="flex items-center gap-2">
                                {group.label}
                                {selectedCount > 0 && (
                                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-foreground px-1.5 text-ui-micro font-medium text-background">
                                    {selectedCount}
                                  </span>
                                )}
                              </span>
                              <ChevronDown
                                className={cn(
                                  "h-4 w-4 text-muted-foreground transition-transform",
                                  isOpen && "rotate-180",
                                )}
                              />
                            </button>
                            {group.id === "tags" && (
                              <button
                                type="button"
                                onClick={handleTagEditModeToggle}
                                className={cn(
                                  "flex h-7 w-7 items-center justify-center rounded-md border border-transparent text-muted-foreground",
                                  "",
                                  isTagEditMode && "bg-muted text-foreground",
                                )}
                                aria-pressed={isTagEditMode}
                                aria-label="Toggle tag edit mode"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                          {isOpen && (
                            <>
                              {isTagEditMode && group.id === "tags" && (
                                <div className={CAPTION}>
                                  Select tags, then tap a post to apply.
                                </div>
                              )}
                              {group.options.length === 0 ? (
                                <p className={META}>{group.emptyLabel}</p>
                              ) : (
                                group.options.map(option => {
                                  const checkboxId = `filter-${group.id}-${option.value.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
                                  return (
                                    <div key={option.value} className="flex items-center justify-between gap-3">
                                      <Label htmlFor={checkboxId} className={cn(ROW_TEXT, "flex-1 font-normal text-foreground")}>
                                        <span className="truncate">{option.label}</span>
                                      </Label>
                                      {typeof option.count === "number" && (
                                        <span className={CAPTION}>{option.count}</span>
                                      )}
                                      <Checkbox
                                        id={checkboxId}
                                        checked={group.selected.includes(option.value)}
                                        onCheckedChange={() => group.onToggle(option.value)}
                                      />
                                    </div>
                                  );
                                })
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </DrawerContent>
            </Drawer>
          )}

          <main className="flex w-full flex-col gap-3 items-center" data-layout-slot={!browseByDefault ? "search-content" : undefined}>
            {feedIsError && (
              <section
                className={cn(
                  META,
                  "rounded border border-destructive/40 bg-destructive/5 p-4 text-destructive",
                  contentWidthClass,
                )}
              >
                <p>
                  {feedError instanceof Error
                    ? feedError.message
                    : "Failed to load shares. Pull to refresh or try again soon."}
                </p>
              </section>
            )}

            {/* Empty State - only show when user has NO posts at all (not just empty filters) */}
            {showEmptyState && (
              <div className={contentWidthClass}>
                {emptyState ?? (
                  <EmptyState
                    headline="Your stashes will appear here"
                    message="Posts, photos, screenshots, links — everything you stash shows up here."
                    variant="overlay"
                    showPlatformButtons
                    showActionButtons
                    dismissKey="stasht_empty_stashes_dismissed"
                  />
                )}
              </div>
            )}

            {/* Search idle state — shown when browseByDefault is false and no query/filters are active */}
            {showSearchIdleState && (
              <div className={cn("py-16 text-center", contentWidthClass)}>
                <h3 className={HEADLINE}>Let's put those saves to work!</h3>
                <p className={cn(META, "mt-1")}>What are you looking for?</p>
              </div>
            )}

            {/* A stash list's own search: in flight, then nothing found. The Search page says both by kind. */}
            {isSearchActive && !searchesByKind && isSearchFetching && !isFetchingNextSearchPage && posts.length === 0 && (
              <div className={cn("py-12 text-center", contentWidthClass)}>
                <h3 className={cn(HEADLINE, "loading-dots")}>Searching</h3>
              </div>
            )}
            {isSearchActive && !searchesByKind && !feedIsLoading && posts.length === 0 && !feedIsError && !isSearchFetching && (
              <div className={cn("py-12 text-center", contentWidthClass)}>
                <p className={META}>No stashes found for "{activeSearch}"</p>
              </div>
            )}

            {searchesByKind ? (
              <div className={cn("w-full", contentWidthClass)} data-layout-slot="search-results">
                {exploring ? (
                  <ExploreResults query={activeSearch} viewMode={resolvedViewMode} />
                ) : (
                  <SearchResults
                    query={activeSearch}
                    facet={facet}
                    onFacet={setFacet}
                    onExplore={() => setCorpus("public")}
                    stashes={{
                      view: renderPosts(
                        facet === "all" ? thumbnailPosts.slice(0, ALL_PREVIEW.stashes) : thumbnailPosts,
                        facet === "stashes",
                      ),
                      // The search's own total arrives with its first page; filters narrow results
                      // here in the browser, so with one on, count what has loaded instead.
                      count:
                        (activeFiltersTotal === 0 ? (searchData?.pages[0] as SearchStashesPage | undefined)?.total : undefined) ??
                        posts.length,
                      loading: isSearchFetching && !isFetchingNextSearchPage,
                      complete:
                        searchData !== undefined &&
                        !isSearchError &&
                        (activeFiltersTotal === 0 || (!hasNextSearchPage && !isSearchFetching)),
                      more: posts.length > ALL_PREVIEW.stashes || !!hasNextSearchPage,
                    }}
                  />
                )}
              </div>
            ) : (
              posts.length > 0 && <div className={cn("w-full", contentWidthClass)}>{renderPosts(thumbnailPosts, true)}</div>
            )}
          </main>
          </div>
          </div>
        </div>
      </PageRefreshBoundary>

      {/* Multi-select action bar */}
      {isSelectMode && (
        <SelectionActionBar
          selectedCount={selectedCount}
          loadedCount={posts.length}
          allLoaded={!feedHasNextPage}
          onSelectAllLoaded={handleSelectAll}
          onDeselectAll={clearSelection}
          onAddToCollection={() => setCollectionSheetOpen(true)}
          onCancel={exitSelectMode}
        />
      )}

      {/* Add to collection sheet for multi-select */}
      <AddToCollectionSheet
        open={collectionSheetOpen}
        onOpenChange={setCollectionSheetOpen}
        items={Array.from(selectedIds).map(id => ({ type: "stash" as const, id }))}
        onBatchComplete={exitSelectMode}
        itemVisibility={batchItemVisibility}
      />
    </>
  );
};

PostsFeed.displayName = "PostsFeed";

export default PostsFeed;
